import { useState } from 'react';

export default function LeadForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    website: '',
    messenger: '',
    service: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const form = e.currentTarget as HTMLFormElement;
      const formDataToSend = new FormData(form);
      
      await fetch('https://readdy.ai/api/form/d4hkhqser93evkarrqo0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formDataToSend as any).toString()
      });
      
      setSubmitStatus('success');
      setFormData({ name: '', phone: '', website: '', messenger: '', service: '' });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus('idle'), 3000);
    }
  };

  return (
    <section id="lead-form" className="py-24 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Ready to Turn Chaos into Stable Growth?
            </h2>
            <p className="text-xl text-gray-700 mb-8 leading-relaxed">
              Leave a request for a <strong className="text-cyan-600">free diagnosis</strong> of your marketing. We will show growth points using AI analysis.
            </p>
            
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-search-eye-line text-2xl text-cyan-600"></i>
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 text-lg mb-1">Deep Analysis</h3>
                  <p className="text-gray-600">We will study your business and competitors using AI</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-presentation-line text-2xl text-cyan-600"></i>
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 text-lg mb-1">Personal Presentation</h3>
                  <p className="text-gray-600">We will show specific growth points and strategy</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-gift-line text-2xl text-cyan-600"></i>
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 text-lg mb-1">Bonus</h3>
                  <p className="text-gray-600">Checklist of quick improvements that can be implemented immediately</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-2xl p-10 border border-gray-100">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-calendar-check-line text-4xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-2">
                Sign Up for Diagnosis
              </h3>
              <p className="text-gray-600">Fill out the form and we will contact you within an hour</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5" data-readdy-form id="lead-form-en">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm"
                  placeholder="+1 (999) 123-45-67"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Service of Interest <span className="text-red-500">*</span>
                </label>
                <select
                  name="service"
                  required
                  value={formData.service}
                  onChange={(e) => setFormData({...formData, service: e.target.value})}
                  className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm cursor-pointer"
                >
                  <option value="">Select a service</option>
                  <option value="Digital Marketing 360°">Digital Marketing 360°</option>
                  <option value="Comprehensive Marketing Strategy">Comprehensive Marketing Strategy</option>
                  <option value="Marketing Consulting">Marketing Consulting</option>
                  <option value="Strategic Management and Partnership">Strategic Management and Partnership</option>
                  <option value="Marketplace Consulting">Marketplace Consulting</option>
                  <option value="Website Development">Website Development</option>
                  <option value="Contextual Advertising">Contextual Advertising</option>
                  <option value="Targeted Advertising">Targeted Advertising</option>
                  <option value="SMM and Content">SMM and Content</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Website (optional)
                </label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={(e) => setFormData({...formData, website: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm"
                  placeholder="https://example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Messenger
                </label>
                <select
                  name="messenger"
                  value={formData.messenger}
                  onChange={(e) => setFormData({...formData, messenger: e.target.value})}
                  className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm cursor-pointer"
                >
                  <option value="">Select messenger</option>
                  <option value="telegram">Telegram</option>
                  <option value="whatsapp">WhatsApp</option>
                </select>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-semibold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Sending...' : 'Sign Up for Diagnosis'}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-xl text-sm">
                  ✓ Request sent! We will contact you shortly.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl text-sm">
                  An error occurred. Please try again.
                </div>
              )}

              <p className="text-xs text-gray-500 text-center">
                By clicking the button, you agree to the privacy policy
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
